'use client';
import React from 'react';
import {
  X, User, Building2, Phone, CreditCard, Clock,
  Car, DoorOpen, Shield, CheckCircle, AlertTriangle,
  MapPin, Users, FileText,
} from 'lucide-react';
import type { Visit } from '@/data/mockData';
import { StatusBadge, TypeBadge } from '@/components/ui/StatusBadge';

interface VisitDetailModalProps {
  visit: Visit;
  onClose: () => void;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mo}/${yy} — ${hh}:${mm}`;
}

interface DetailRowProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

function DetailRow({ icon, label, value }: DetailRowProps) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border last:border-0">
      <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
        <span className="text-muted-foreground">{icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">{label}</p>
        <div className="text-sm text-foreground">{value}</div>
      </div>
    </div>
  );
}

export default function VisitDetailModal({ visit, onClose }: VisitDetailModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="visit-detail-title"
    >
      <div
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent/15 flex items-center justify-center">
              <FileText size={18} className="text-accent" />
            </div>
            <div>
              <h3 id="visit-detail-title" className="font-bold text-foreground text-lg">تفاصيل الزيارة</h3>
              <p className="text-xs text-muted-foreground font-mono">{visit.id}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={visit.status} />
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Visitor info */}
          <div>
            <h4 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
              <User size={14} className="text-accent" />
              بيانات الزائر
            </h4>
            <div className="bg-muted/30 border border-border rounded-xl p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                  <User size={22} className="text-accent" />
                </div>
                <div>
                  <p className="font-bold text-foreground text-lg">{visit.visitorName}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <TypeBadge type={visit.type} size="sm" />
                  </div>
                </div>
              </div>
              <div className="space-y-0">
                <DetailRow
                  icon={<Building2 size={13} />}
                  label="الجهة"
                  value={visit.company}
                />
                <DetailRow
                  icon={<CreditCard size={13} />}
                  label="الرقم القومي"
                  value={<span className="font-mono tabular-nums">{visit.nationalId}</span>}
                />
                <DetailRow
                  icon={<Phone size={13} />}
                  label="رقم الموبايل"
                  value={<span className="font-mono tabular-nums">{visit.mobile}</span>}
                />
              </div>
            </div>
          </div>

          {/* Visit details */}
          <div>
            <h4 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
              <FileText size={14} className="text-accent" />
              تفاصيل الزيارة
            </h4>
            <div className="bg-muted/30 border border-border rounded-xl p-4 space-y-0">
              <DetailRow
                icon={<MapPin size={13} />}
                label="غرض الزيارة"
                value={visit.purpose}
              />
              <DetailRow
                icon={<Building2 size={13} />}
                label="القسم المضيف"
                value={
                  <div>
                    <p>{visit.hostDepartment}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{visit.hostEmployee}</p>
                  </div>
                }
              />
              <DetailRow
                icon={<DoorOpen size={13} />}
                label="البوابة"
                value={visit.gate}
              />
              <DetailRow
                icon={<Shield size={13} />}
                label="الموظف الأمني"
                value={visit.securityOfficer}
              />
              {visit.vehiclePlate && (
                <DetailRow
                  icon={<Car size={13} />}
                  label="رقم المركبة"
                  value={<span className="font-mono">{visit.vehiclePlate}</span>}
                />
              )}
              <DetailRow
                icon={<Users size={13} />}
                label="عدد الركاب"
                value={<span className="tabular-nums">{visit.passengersCount} {visit.passengersCount === 1 ? 'شخص' : 'أشخاص'}</span>}
              />
              <DetailRow
                icon={<CreditCard size={13} />}
                label="رقم الكارت"
                value={<span className="font-mono text-accent">{visit.badgeNumber}</span>}
              />
            </div>
          </div>

          {/* Timing */}
          <div>
            <h4 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
              <Clock size={14} className="text-accent" />
              التوقيت
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-success/5 border border-success/20 rounded-xl p-3 text-center">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">وقت الدخول</p>
                <p className="text-sm font-bold text-success tabular-nums">{formatDateTime(visit.entryTime)}</p>
              </div>
              <div className={`rounded-xl p-3 text-center ${visit.exitTime ? 'bg-muted/30 border border-border' : 'bg-muted/10 border border-dashed border-border'}`}>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">وقت الخروج</p>
                <p className="text-sm font-bold text-muted-foreground tabular-nums">
                  {visit.exitTime ? formatDateTime(visit.exitTime) : '—'}
                </p>
              </div>
              <div className="bg-accent/5 border border-accent/20 rounded-xl p-3 text-center">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">المدة الإجمالية</p>
                <p className="text-sm font-bold text-accent tabular-nums">
                  {visit.duration ?? (visit.status === 'داخل' ? 'جارية' : '—')}
                </p>
              </div>
            </div>
          </div>

          {/* Safety & notes */}
          <div className="space-y-3">
            <div className={`flex items-center gap-3 p-3 rounded-xl border ${
              visit.safetyAcknowledged
                ? 'bg-success/5 border-success/20' :'bg-danger/5 border-danger/20'
            }`}>
              {visit.safetyAcknowledged ? (
                <CheckCircle size={16} className="text-success shrink-0" />
              ) : (
                <AlertTriangle size={16} className="text-danger shrink-0" />
              )}
              <p className={`text-sm font-semibold ${visit.safetyAcknowledged ? 'text-success' : 'text-danger'}`}>
                {visit.safetyAcknowledged ? 'تم توقيع إقرار السلامة' : 'لم يُوقَّع إقرار السلامة'}
              </p>
            </div>

            {visit.notes && (
              <div className="bg-warning/5 border border-warning/20 rounded-xl p-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">ملاحظات</p>
                <p className="text-sm text-foreground leading-relaxed">{visit.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 pt-0">
          <button
            onClick={onClose}
            className="w-full py-3 bg-muted text-foreground rounded-xl font-semibold hover:bg-muted/70 transition-all duration-150 active:scale-[0.98]"
          >
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
}